import { z } from 'zod';
import { PLANS, PLAN_IDS, type PlanId } from './plans.js';

/**
 * Facturacion: lo que comparten el panel y el servidor.
 *
 * Aqui NO hay precios ni limites. Los precios estan en `plans.ts`, que es la
 * fuente unica, y el importe que de verdad se cobra lo fija la variante en el
 * panel de Lemon Squeezy: nosotros mandamos el identificador de la variante y
 * nunca una cantidad. Si el importe viajara desde el navegador, cualquiera
 * podria contratar el plan Negocios por un dolar.
 */

export const CICLOS_DE_COBRO = ['mensual', 'anual'] as const;
export type CicloDeCobro = (typeof CICLOS_DE_COBRO)[number];

/**
 * Planes que se contratan pagando.
 *
 * Se deduce del precio en vez de escribir la lista a mano: el gratuito no se
 * contrata y la cortesia no se vende, y ambos quedan fuera solos. Asi, anadir
 * un plan nuevo a `plans.ts` no obliga a acordarse de este archivo.
 */
export function esPlanDePago(plan: PlanId): boolean {
  const precio = PLANS[plan].monthlyPriceUsdCents;
  return precio !== null && precio > 0;
}

export function planesDePago(): readonly PlanId[] {
  return PLAN_IDS.filter(esPlanDePago);
}

/**
 * Estados de una suscripcion, tal y como los nombra Lemon Squeezy.
 *
 * Se guardan con su nombre original y no traducidos: son los que llegan en el
 * webhook y los que se ven en su panel, y renombrarlos obligaria a traducir en
 * los dos sentidos cada vez que hay que cotejar una cuenta con su proveedor,
 * que es justo lo que se hace cuando un cliente reclama.
 */
export const ESTADOS_DE_SUSCRIPCION = [
  'on_trial',
  'active',
  'paused',
  'past_due',
  'unpaid',
  'cancelled',
  'expired',
] as const;
export type EstadoDeSuscripcion = (typeof ESTADOS_DE_SUSCRIPCION)[number];

/**
 * Forma del documento `orgs/{orgId}/billing/suscripcion`.
 *
 * Las fechas se guardan como cadenas ISO y no como Timestamp porque llegan asi
 * del proveedor: convertirlas a Timestamp solo para que el panel las vuelva a
 * convertir anade dos conversiones donde puede perderse la zona horaria, y lo
 * unico que se hace con ellas es mostrarlas.
 *
 * NO contiene ningun dato de tarjeta mas alla de la marca y los cuatro ultimos
 * digitos, que es lo que manda el proveedor para que el cliente reconozca con
 * que paga. Nunca el numero completo, ni el CVV, ni la fecha de caducidad.
 */
export interface Suscripcion {
  readonly proveedor: 'lemonsqueezy';
  readonly subscriptionId: string;
  readonly customerId: string | null;
  readonly orderId: string | null;
  readonly variantId: string;
  /** El plan que esta suscripcion concede, ya traducido desde la variante. */
  readonly plan: PlanId;
  readonly ciclo: CicloDeCobro | null;
  readonly estado: EstadoDeSuscripcion;
  /** Proximo cobro, en ISO. */
  readonly renuevaEl: string | null;
  /** Cuando deja de dar acceso, si esta cancelada. */
  readonly terminaEl: string | null;
  readonly pruebaHasta: string | null;
  /** Cancelada pero vigente hasta `terminaEl`. */
  readonly cancelaAlFinal: boolean;
  readonly tarjeta: { readonly marca: string | null; readonly ultimos4: string | null } | null;
  readonly ultimoCobroEl: string | null;
  readonly ultimoCobroFallidoEl: string | null;
  /** Suscripcion de la tienda de pruebas: no corresponde a dinero real. */
  /**
   * Cuando cambio en el lado del proveedor, tal y como el lo cuenta.
   *
   * No es lo mismo que `actualizadoEl`, que es cuando lo escribimos nosotros.
   * Esta sirve para comparar dos avisos entre si y quedarse con el mas nuevo,
   * porque Lemon Squeezy no garantiza el orden de entrega.
   */
  readonly cambiadaEl: string | null;
  readonly modoPrueba: boolean;
}

/** Si el estado da derecho a usar el plan ahora mismo. */
export function daAcceso(estado: EstadoDeSuscripcion): boolean {
  // `past_due` y `cancelled` siguen dando acceso a proposito: en el primero el
  // proveedor aun esta reintentando el cobro, y en el segundo el cliente ya
  // pago el periodo en curso. Cortar antes de tiempo es quitarle el servicio a
  // quien esta al dia, y en este producto significa apagar codigos impresos.
  return estado === 'active' || estado === 'on_trial' || estado === 'past_due' || estado === 'cancelled';
}

export const iniciarPagoSchema = z.object({
  orgId: z.string().min(1).max(64),
  plan: z
    .enum(PLAN_IDS)
    .refine(esPlanDePago, 'Ese plan no se contrata desde aqui.'),
  ciclo: z.enum(CICLOS_DE_COBRO),
});
export type IniciarPagoInput = z.infer<typeof iniciarPagoSchema>;

export const portalDeFacturacionSchema = z.object({
  orgId: z.string().min(1).max(64),
});
export type PortalDeFacturacionInput = z.infer<typeof portalDeFacturacionSchema>;

export const cancelarSuscripcionSchema = z.object({
  orgId: z.string().min(1).max(64),
});
export type CancelarSuscripcionInput = z.infer<typeof cancelarSuscripcionSchema>;

export const reembolsarPedidoSchema = z.object({
  orgId: z.string().min(1).max(64),
  orderId: z.string().min(1).max(64),
  motivo: z.string().min(3).max(300),
});
export type ReembolsarPedidoInput = z.infer<typeof reembolsarPedidoSchema>;
